import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext } from 'react-router-dom'
import { partnerApiService } from '@/services/partnerApiService'
import {
  Search,
  Filter,
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar
} from 'lucide-react'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { fmtByCurrency, orDash } from '@/utils/currency'
import { motion, AnimatePresence } from 'framer-motion'

interface Partner {
  id: number
  name: string
  company?: string
  can_view_users: boolean
  can_view_trading: boolean
  can_view_deposits: boolean
  can_view_withdrawals: boolean
  can_view_fees: boolean
  can_view_statistics: boolean
}

interface SymbolOption {
  symbol: string
  display_name?: string
}

interface Trade {
  id: number
  user_email: string
  user_nickname: string
  position_id?: number
  symbol: string
  action_type?: string  // OPEN, FULL_CLOSE, PARTIAL_CLOSE
  side: string
  order_type?: string
  margin_type: string
  margin_amount: number | null
  leverage: number | null
  currency?: string   // 'KRW'(신선물) | 'USDT'(기존시장) — 2026-08-31 병합
  market?: string     // 'NF' | 'LEGACY'
  quantity?: number
  price?: number
  entry_price?: number
  exit_price?: number
  liquidation_price?: number
  fee?: number
  pnl?: number
  pnl_percentage?: number
  status?: string
  ordered_at?: string
  filled_at?: string
  created_at: string
  closed_at?: string
}

interface TradingResponse {
  trades: Trade[]
  total: number
  page: number
  pages: number
}

const PartnerTrading = () => {
  const { partner } = useOutletContext<{ partner: Partner }>()
  const { t, i18n } = useTranslation('trading')
  const locale = i18n.language === 'ko' ? 'ko-KR' : i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'zh' ? 'zh-CN' : 'en-US'
  const [trades, setTrades] = useState<Trade[]>([])
  const [loading, setLoading] = useState(true)
  const [searching, setSearching] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [symbolFilter, setSymbolFilter] = useState<string>('')
  const [sideFilter, setSideFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalTrades, setTotalTrades] = useState(0)
  const [symbols, setSymbols] = useState<SymbolOption[]>([])
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // 권한 확인
  if (!partner.can_view_trading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-2">{t('common:noPermission')}</h2>
          <p className="text-dark-400">{t('noPermission')}</p>
        </div>
      </div>
    )
  }

  useEffect(() => {
    loadTrades()
  }, [currentPage, search, symbolFilter, sideFilter, statusFilter])

  useEffect(() => {
    loadSymbols()
  }, [])

  // 검색 디바운스 처리
  const handleSearch = (input: string) => {
    setSearchInput(input)
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearch(input)
      setCurrentPage(1) // 검색 시 첫 페이지로 이동
    }, 300)
  }

  const loadSymbols = async () => {
    try {
      const data = await partnerApiService.getTradingSymbols()
      // 백엔드가 이미 {symbol, display_name} 객체 배열을 반환
      const symbolOptions: SymbolOption[] = (data.symbols || []).map((s: any) => {
        // s가 문자열인 경우와 객체인 경우 모두 처리
        if (typeof s === 'string') {
          return { symbol: s, display_name: s }
        }
        return { symbol: s.symbol, display_name: s.display_name || s.symbol }
      })
      setSymbols(symbolOptions)
    } catch (error) {
      console.error('Error loading symbols:', error)
    }
  }

  const loadTrades = async () => {
    try {
      if (currentPage === 1 && !search && !symbolFilter && !sideFilter && !statusFilter) {
        setLoading(true)
      } else {
        setSearching(true)
      }

      console.log('🔍 PartnerTrading: 거래 내역 요청 시작', {
        currentPage,
        search,
        symbolFilter,
        sideFilter,
        statusFilter
      })

      const data = await partnerApiService.getTrading(
        currentPage,
        10,
        symbolFilter || undefined,
        sideFilter || undefined,
        statusFilter || undefined,
        search || undefined
      )

      console.log('✅ PartnerTrading: 거래 내역 가져오기 성공', data)
      console.log('Trades length:', data.trades?.length)
      console.log('Total trades:', data.total)
      console.log('Pages:', data.pages)

      // PartnerTrade[] -> Trade[] 변환 (타입 호환)
      setTrades(data.trades as Trade[])
      setTotalPages(data.pages)
      setTotalTrades(data.total)
    } catch (error) {
      console.error('❌ Error loading trades:', error)
      console.error('Error details:', JSON.stringify(error, null, 2))
    } finally {
      console.log('🏁 Loading complete, setting loading/searching to false')
      setLoading(false)
      setSearching(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount) + ' USDT'
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price)
  }

  // 통화별 진입가: 신선물(KRW)=원 / 기존시장(USDT)=$
  const formatEntryPrice = (trade: Trade) => {
    if (!trade.entry_price) return '-'
    return trade.currency === 'KRW'
      ? `${formatPrice(trade.entry_price)}원`
      : `$${formatPrice(trade.entry_price)}`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(locale)
  }

  const getSideColor = (side: string) => {
    // 국내 HTS 관례: 롱=빨강 / 숏=파랑 (PartnerRealtime와 동일)
    return side === 'LONG'
      ? 'bg-red-500/20 text-red-400 border-red-500/30'
      : 'bg-blue-500/20 text-blue-400 border-blue-500/30'
  }

  const getSideIcon = (side: string) => {
    return side === 'LONG' ? TrendingUp : TrendingDown
  }

  const getActionColor = (action?: string) => {
    switch (action) {
      case 'OPEN':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      case 'FULL_CLOSE':
        return 'bg-green-500/20 text-green-400 border-green-500/30'
      case 'PARTIAL_CLOSE':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    }
  }

  const getActionText = (action?: string) => {
    if (!action) return 'N/A'
    const actionMap: { [key: string]: string } = {
      'OPEN': t('actionOpen'),
      'FULL_CLOSE': t('actionFullClose'),
      'PARTIAL_CLOSE': t('actionPartialClose')
    }
    return actionMap[action] || action
  }

  const getPnlColor = (pnl?: number) => {
    // 국내 HTS 관례: 이익=빨강 / 손실=파랑
    if (!pnl) return 'text-dark-400'
    return pnl > 0 ? 'text-red-400' : 'text-blue-400'
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      {/* 페이지 헤더 */}
      <div className="border-b border-white/10 pb-4 sm:pb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{t('title')}</h1>
        <p className="text-dark-400">{t('subtitle')}</p>
      </div>

      {/* 검색 및 필터 */}
      <div className="glass-card p-4 sm:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* 검색 */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-dark-400" />
            <input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={searchInput}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-dark-800 border border-white/10 rounded-lg text-white placeholder-dark-400 focus:border-primary-500 focus:outline-none"
            />
          </div>

          {/* 심볼 필터 */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-dark-400" />
            <select
              value={symbolFilter}
              onChange={(e) => {
                setSymbolFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="w-full pl-10 pr-8 py-3 bg-dark-800 border border-white/10 rounded-lg text-white focus:border-primary-500 focus:outline-none"
            >
              <option value="">{t('allSymbols')}</option>
              {symbols.map(symbolOption => (
                <option key={symbolOption.symbol} value={symbolOption.symbol}>
                  {symbolOption.display_name || symbolOption.symbol}
                </option>
              ))}
            </select>
          </div>

          {/* 방향 필터 */}
          <div className="relative">
            <select
              value={sideFilter}
              onChange={(e) => {
                setSideFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="w-full pl-4 pr-8 py-3 bg-dark-800 border border-white/10 rounded-lg text-white focus:border-primary-500 focus:outline-none"
            >
              <option value="">{t('allDirections')}</option>
              <option value="LONG">{t('longOption')}</option>
              <option value="SHORT">{t('shortOption')}</option>
            </select>
          </div>

          {/* 상태 필터 */}
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value)
                setCurrentPage(1)
              }}
              className="w-full pl-4 pr-8 py-3 bg-dark-800 border border-white/10 rounded-lg text-white focus:border-primary-500 focus:outline-none"
            >
              <option value="">{t('allStatus')}</option>
              <option value="OPEN">{t('actionOpen')}</option>
              <option value="FULL_CLOSE">{t('actionFullClose')}</option>
              <option value="PARTIAL_CLOSE">{t('actionPartialClose')}</option>
            </select>
          </div>
        </div>

        {/* 검색/필터 결과 정보 */}
        <div className="mt-4 text-sm text-dark-400">
          {t('totalTrades', { count: totalTrades })}
          {search && ` · ${t('searchResult', { search })}`}
          {(symbolFilter || sideFilter || statusFilter) && ` · ${t('filterApplied')}`}
        </div>
      </div>

      {/* 거래 목록 */}
      <div className="glass-card relative">
        {/* 로딩 오버레이 */}
        <AnimatePresence>
          {searching && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-dark-900/50 backdrop-blur-sm rounded-lg flex items-center justify-center z-10"
            >
              <LoadingSpinner />
            </motion.div>
          )}
        </AnimatePresence>

        {/* 테이블 헤더 */}
        <div className="p-4 sm:p-6 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              {t('tradeHistory')}
            </h3>
            <div className="text-sm text-dark-400">
              {t('common:pageInfo', { current: currentPage, total: totalPages })}
            </div>
          </div>
        </div>

        {/* 데스크톱 테이블 */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('tableUser')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('tableSymbolDirection')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('tableMarginLeverage')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('tableEntryPrice')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('tablePnl')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('tableFee')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider w-24">
                  {t('tableStatus')}
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-dark-300 uppercase tracking-wider">
                  {t('tableTime')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {trades.map((trade) => {
                const SideIcon = getSideIcon(trade.side)
                return (
                  <tr key={trade.id} className="hover:bg-white/5 transition-colors duration-200">
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-white">{trade.user_nickname}</div>
                        <div className="text-sm text-dark-400">{trade.user_email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="mr-3">
                          <div className="text-sm font-medium text-white">{trade.symbol}{trade.market === 'NF' && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-300 align-middle">{t('nfBadge')}</span>}</div>
                          <div className="flex items-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center ${getSideColor(trade.side)}`}>
                              <SideIcon className="w-3 h-3 mr-1" />
                              {trade.side}
                            </span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-white">{trade.margin_amount != null ? fmtByCurrency(trade.margin_amount, trade.currency) : '-'}</div>
                        <div className="text-sm text-dark-400">{trade.leverage != null ? `${trade.leverage}x` : orDash(null)}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-white">
                        {formatEntryPrice(trade)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {trade.action_type !== 'OPEN' && trade.pnl !== undefined && trade.pnl !== null ? (
                        <div>
                          <div className={`text-sm font-medium ${getPnlColor(trade.pnl)}`}>
                            {fmtByCurrency(trade.pnl, trade.currency, true)}
                          </div>
                          {trade.pnl_percentage !== undefined && trade.pnl_percentage !== null && (
                            <div className={`text-xs ${getPnlColor(trade.pnl)}`}>
                              ({trade.pnl_percentage > 0 ? '+' : ''}{trade.pnl_percentage.toFixed(2)}%)
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-dark-400">-</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-white">{fmtByCurrency(trade.fee ?? 0, trade.currency)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap w-24">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${getActionColor(trade.action_type)}`}>
                        {getActionText(trade.action_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="flex items-center text-sm text-dark-300">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(trade.created_at)}
                        </div>
                        {trade.closed_at && (
                          <div className="text-xs text-dark-500 mt-1">
                            {t('closed', { date: formatDate(trade.closed_at) })}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* 모바일 카드 목록 */}
        <div className="lg:hidden divide-y divide-white/10">
          {trades.map((trade) => {
            const SideIcon = getSideIcon(trade.side)
            return (
              <div key={trade.id} className="p-4 sm:p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <div className="text-lg font-medium text-white mr-3">{trade.symbol}{trade.market === 'NF' && <span className="ml-1.5 text-[10px] px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-300 align-middle">{t('nfBadge')}</span>}</div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border flex items-center ${getSideColor(trade.side)}`}>
                        <SideIcon className="w-3 h-3 mr-1" />
                        {trade.side}
                      </span>
                    </div>
                    <div className="text-sm text-dark-400">{trade.user_nickname}</div>
                    <div className="text-xs text-dark-500">{trade.user_email}</div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getActionColor(trade.action_type)}`}>
                    {getActionText(trade.action_type)}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <div className="text-dark-400 mb-1">{t('marginAmount')}</div>
                    <div className="text-white font-medium">{trade.margin_amount != null ? fmtByCurrency(trade.margin_amount, trade.currency) : '-'}</div>
                  </div>
                  <div>
                    <div className="text-dark-400 mb-1">{t('leverage')}</div>
                    <div className="text-white font-medium">{trade.leverage != null ? `${trade.leverage}x` : '-'}</div>
                  </div>
                  <div>
                    <div className="text-dark-400 mb-1">{t('entryPrice')}</div>
                    <div className="text-white font-medium">
                      {formatEntryPrice(trade)}
                    </div>
                  </div>
                  <div>
                    <div className="text-dark-400 mb-1">{t('pnl')}</div>
                    {trade.action_type !== 'OPEN' && trade.pnl !== undefined && trade.pnl !== null ? (
                      <div className={`font-medium ${getPnlColor(trade.pnl)}`}>
                        {fmtByCurrency(trade.pnl, trade.currency, true)}
                      </div>
                    ) : (
                      <div className="text-dark-400">-</div>
                    )}
                  </div>
                  <div>
                    <div className="text-dark-400 mb-1">{t('fee')}</div>
                    <div className="text-white font-medium">{fmtByCurrency(trade.fee ?? 0, trade.currency)}</div>
                  </div>
                </div>
                
                <div className="text-sm">
                  <div className="text-dark-400 mb-1">{t('tradeTime')}</div>
                  <div className="text-white">{formatDate(trade.created_at)}</div>
                  {trade.closed_at && (
                    <div className="text-xs text-dark-500 mt-1">
                      {t('closed', { date: formatDate(trade.closed_at) })}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* 빈 상태 */}
        {trades.length === 0 && !searching && (
          <div className="p-8 sm:p-12 text-center">
            <BarChart3 className="w-12 h-12 text-dark-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">{t('noTrades')}</h3>
            <p className="text-dark-400">
              {search || symbolFilter || sideFilter || statusFilter
                ? t('noMatchingTrades')
                : t('noTradesYet')}
            </p>
          </div>
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-dark-800 border border-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-dark-700 transition-colors duration-200"
          >
            {t('common:previous')}
          </button>

          <div className="flex space-x-1">
            {(() => {
              const pages = []
              const maxVisible = 5
              let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2))
              let endPage = Math.min(totalPages, startPage + maxVisible - 1)

              // 끝 페이지가 totalPages보다 작으면 시작 페이지 조정
              if (endPage - startPage < maxVisible - 1) {
                startPage = Math.max(1, endPage - maxVisible + 1)
              }

              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i)}
                    className={`px-4 py-2 border border-white/10 rounded-lg transition-colors duration-200 ${
                      currentPage === i
                        ? 'bg-primary-500 text-white border-primary-500'
                        : 'bg-dark-800 text-white hover:bg-dark-700'
                    }`}
                  >
                    {i}
                  </button>
                )
              }

              return pages
            })()}
          </div>

          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-dark-800 border border-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-dark-700 transition-colors duration-200"
          >
            {t('common:next')}
          </button>
        </div>
      )}
    </div>
  )
}

export default PartnerTrading